import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../../api/client";
import type {
  BuildDeckRating,
  CatalogCard,
  DeckRunSummary,
  DeckSlot,
  DeckState,
  SlotBrief,
} from "../../api/client";
import { Card, naturalCardWidthPx } from "../Card";
import { Inspector } from "../Inspector";
import { catalogToCardView } from "../../logic/catalogToCardView";
import { pixelTheme, type Theme } from "../../theme/pixel";
import { defaultBtn, primaryBtn } from "../../theme/styles";
import { AnimatedEllipsis } from "./AnimatedEllipsis";
import { Banner, Field, NumberInput, Select, TextArea, TextInput } from "./forms";

// BUILD DECK WITH AI — the architect + per-slot card-gen tool that
// replaces the legacy big-bang NewDeckTool. Flow:
//
//   1. Admin types slug, faction, theme. Clicks ARCHITECT.
//   2. Server runs one LLM call → tools/deck_gen/runs/<slug>/architect.json
//      and seeds deck.json with one entry per slot (no candidates yet).
//   3. Admin clicks GENERATE on a slot row. Server runs card-gen with the
//      slot's brief as a prefilled-json. Cards land in cards/gen/<slug>/.
//   4. Admin picks a candidate (KEEP) or EDIT-the-card via the card
//      editor. Repeat until every slot has a chosen card.
//   5. Optional: RATE the deck against a per-slug deterministic cohort.
//   6. PROMOTE → cards move to npc_cards/<faction>/ and the deck yaml
//      lands in world/decks/npc/<slug>.yaml.

const FACTIONS = ["humans", "goblins", "dwarves", "orcs", "elves"];
const CARD_TYPES: SlotBrief["card_type"][] = [
  "creature",
  "spell",
  "enchantment",
  "trap",
];
const RARITIES: SlotBrief["rarity"][] = [
  "common",
  "uncommon",
  "rare",
  "legendary",
];

const POLL_INTERVAL_MS = 2000;

export function BuildDeckAi() {
  const theme = pixelTheme;
  const navigate = useNavigate();

  // --- form state for the architect call ---
  const [slug, setSlug] = useState("");
  const [faction, setFaction] = useState("goblins");
  const [theme_, setThemeText] = useState("");
  const [deckSize, setDeckSize] = useState("30");
  const [copyCap, setCopyCap] = useState("4");
  const [k, setK] = useState("2");

  // --- pipeline state ---
  const [architectJobId, setArchitectJobId] = useState<string | null>(null);
  const [architectError, setArchitectError] = useState<string | null>(null);
  const [deckState, setDeckState] = useState<DeckState | null>(null);
  const [slotJobIds, setSlotJobIds] = useState<Record<number, string>>({});
  const [slotErrors, setSlotErrors] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingBrief, setEditingBrief] = useState<Set<number>>(new Set());

  // --- promote / sandbox / rate state ---
  const [npcId, setNpcId] = useState("");
  const [npcOptions, setNpcOptions] = useState<api.AdminNpcSummary[]>([]);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteResult, setPromoteResult] =
    useState<api.FinalizeDeckResult | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [rating, setRating] = useState<BuildDeckRating | null>(null);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [ratingError, setRatingError] = useState<string | null>(null);

  // --- inspector / catalog lookup for candidates ---
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  const [catalogById, setCatalogById] =
    useState<Record<string, CatalogCard>>({});

  // Card-ids whose card.png is currently being regenerated. Tracked so
  // the per-candidate REGEN IMAGE button can show its own spinner
  // without blocking everything else.
  const [regenInFlight, setRegenInFlight] = useState<Set<string>>(new Set());

  // Existing runs picker. Refetched on mount, after every architect /
  // slot job completes, and after delete-run.
  const [runs, setRuns] = useState<DeckRunSummary[]>([]);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);

  const refreshRuns = useCallback(async () => {
    try {
      const { runs: r } = await api.listDeckRuns();
      setRuns(r);
      setRunsError(null);
    } catch (e) {
      setRunsError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  // Load NPC list for the promote dropdown.
  useEffect(() => {
    let cancelled = false;
    api
      .listAdminNpcDecks()
      .then(({ npcs }) => !cancelled && setNpcOptions(npcs))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch deck.json for the active slug. Called after every job
  // completes and whenever the user switches slugs.
  const refreshState = useCallback(async (s: string) => {
    if (!s) {
      setDeckState(null);
      return;
    }
    try {
      const ds = await api.getDeckState(s);
      setDeckState(ds);
    } catch (e: unknown) {
      if (e instanceof api.ApiError && e.status === 404) {
        setDeckState(null);
      } else {
        setArchitectError(e instanceof Error ? e.message : String(e));
      }
    }
  }, []);

  // Auto-load existing run state whenever the slug input changes. If
  // the admin types an existing slug, they see its in-progress deck.
  // If they type a new slug, they see the blank "no run yet" state.
  // 350ms debounce so we don't fire a request per keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      void refreshState(slug.trim());
    }, 350);
    return () => window.clearTimeout(handle);
  }, [slug, refreshState]);

  // Pull catalog metadata for every candidate referenced by the state
  // so we can render them as actual cards. Skips ids we already have.
  useEffect(() => {
    if (!deckState) return;
    const ids = new Set<string>();
    deckState.slots.forEach((s) =>
      s.candidates.forEach((c) => ids.add(c.card_id)),
    );
    const missing = [...ids].filter((id) => !(id in catalogById));
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.all(
      missing.map((id) =>
        api.getAdminCardCatalog(id).catch(() => null as CatalogCard | null),
      ),
    ).then((cards) => {
      if (cancelled) return;
      setCatalogById((prev) => {
        const next = { ...prev };
        cards.forEach((c, i) => {
          if (c) next[missing[i]] = c;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [deckState, catalogById]);

  // Poll the architect job to completion, then refetch state.
  useEffect(() => {
    if (!architectJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { jobs } = await api.listAdminJobs();
        const j = jobs.find((x) => x.id === architectJobId);
        if (!j) return;
        if (cancelled) return;
        if (j.status === "succeeded" || j.status === "failed") {
          if (j.status === "failed") {
            setArchitectError(j.error ?? "architect job failed");
          }
          setArchitectJobId(null);
          await refreshState(slug);
          await refreshRuns();
        }
      } catch (e) {
        if (!cancelled) {
          setArchitectError(e instanceof Error ? e.message : String(e));
        }
      }
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [architectJobId, slug, refreshState]);

  // Poll each in-flight slot-card job. We can have several at once;
  // each entry in slotJobIds is independent.
  useEffect(() => {
    const entries = Object.entries(slotJobIds);
    if (entries.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { jobs } = await api.listAdminJobs();
        if (cancelled) return;
        const next = { ...slotJobIds };
        let stateDirty = false;
        for (const [idxStr, jid] of entries) {
          const j = jobs.find((x) => x.id === jid);
          if (!j) continue;
          if (j.status === "succeeded" || j.status === "failed") {
            delete next[Number(idxStr)];
            stateDirty = true;
            if (j.status === "failed") {
              setSlotErrors((prev) => ({
                ...prev,
                [Number(idxStr)]: j.error ?? "slot generation failed",
              }));
            }
          }
        }
        if (stateDirty) {
          setSlotJobIds(next);
          await refreshState(slug);
          await refreshRuns();
        }
      } catch (e) {
        if (!cancelled) {
          // Surface to whichever slots were in flight at the time of
          // the error.
          for (const idxStr of Object.keys(slotJobIds)) {
            setSlotErrors((prev) => ({
              ...prev,
              [Number(idxStr)]: e instanceof Error ? e.message : String(e),
            }));
          }
        }
      }
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [slotJobIds, slug, refreshState]);

  // --- handlers ---

  const onArchitect = useCallback(async () => {
    setArchitectError(null);
    setRating(null);
    setRatingError(null);
    setPromoteResult(null);
    setPromoteError(null);
    setSlotErrors({});
    try {
      const { job_id } = await api.enqueueArchitectDeck({
        slug,
        faction,
        theme: theme_,
        deck_size: Number(deckSize) || undefined,
        copy_cap: Number(copyCap) || undefined,
      });
      setArchitectJobId(job_id);
    } catch (e) {
      setArchitectError(e instanceof Error ? e.message : String(e));
    }
  }, [slug, faction, theme_, deckSize, copyCap]);

  const onGenerateSlot = useCallback(
    async (slot: DeckSlot) => {
      if (!deckState) return;
      setSlotErrors((prev) => {
        const n = { ...prev };
        delete n[slot.idx];
        return n;
      });
      try {
        const { job_id } = await api.enqueueGenerateSlotCard({
          slug: deckState.slug,
          slot_idx: slot.idx,
          brief: slot.brief,
          k: Number(k) || 2,
        });
        setSlotJobIds((prev) => ({ ...prev, [slot.idx]: job_id }));
      } catch (e) {
        setSlotErrors((prev) => ({
          ...prev,
          [slot.idx]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [deckState, k],
  );

  const onKeep = useCallback(
    async (slotIdx: number, cardId: string) => {
      if (!deckState) return;
      try {
        const next = await api.patchDeckState(deckState.slug, {
          set_chosen: { idx: slotIdx, card_id: cardId },
        });
        setDeckState(next);
      } catch (e) {
        setSlotErrors((prev) => ({
          ...prev,
          [slotIdx]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [deckState],
  );

  const onSetCount = useCallback(
    async (slotIdx: number, count: number) => {
      if (!deckState) return;
      try {
        const next = await api.patchDeckState(deckState.slug, {
          set_count: { idx: slotIdx, count },
        });
        setDeckState(next);
      } catch (e) {
        setSlotErrors((prev) => ({
          ...prev,
          [slotIdx]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [deckState],
  );

  const onEditCard = useCallback(
    (cardId: string) => {
      if (!deckState) return;
      const returnTo = `/admin/tools/new_deck?slug=${encodeURIComponent(
        deckState.slug,
      )}`;
      navigate(
        `/admin/card-editor/${encodeURIComponent(cardId)}/edit?returnTo=${encodeURIComponent(
          returnTo,
        )}`,
      );
    },
    [deckState, navigate],
  );

  const onRegenImage = useCallback(async (cardId: string) => {
    setRegenInFlight((prev) => new Set(prev).add(cardId));
    try {
      const { job_id } = await api.enqueueRegenImage(cardId);
      // Poll until done; bump catalog cache-busting so the refreshed
      // png shows up. Keep the polling lightweight — `listAdminJobs`
      // is already the page-wide poll target.
      const tick = async () => {
        const { jobs } = await api.listAdminJobs();
        const j = jobs.find((x) => x.id === job_id);
        if (!j) return false;
        return j.status === "succeeded" || j.status === "failed";
      };
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        try {
          done = await tick();
        } catch {
          done = true;
        }
      }
      // Drop the cached catalog entry so the next render fetches a
      // fresh image URL (the engine bumps the cache key on regen).
      setCatalogById((prev) => {
        const n = { ...prev };
        delete n[cardId];
        return n;
      });
    } finally {
      setRegenInFlight((prev) => {
        const n = new Set(prev);
        n.delete(cardId);
        return n;
      });
    }
  }, []);

  const onSaveBrief = useCallback(
    async (slotIdx: number, brief: SlotBrief) => {
      if (!deckState) return;
      try {
        const next = await api.patchDeckState(deckState.slug, {
          set_brief: { idx: slotIdx, brief },
        });
        setDeckState(next);
        setEditingBrief((prev) => {
          const n = new Set(prev);
          n.delete(slotIdx);
          return n;
        });
      } catch (e) {
        setSlotErrors((prev) => ({
          ...prev,
          [slotIdx]: e instanceof Error ? e.message : String(e),
        }));
      }
    },
    [deckState],
  );

  const onRate = useCallback(async () => {
    if (!deckState) return;
    setRatingBusy(true);
    setRatingError(null);
    try {
      const r = await api.rateDeck(deckState.slug);
      setRating(r);
    } catch (e) {
      setRatingError(e instanceof Error ? e.message : String(e));
    } finally {
      setRatingBusy(false);
    }
  }, [deckState]);

  const onFinalize = useCallback(async () => {
    if (!deckState) return;
    if (!npcId) {
      setPromoteError("pick an NPC first");
      return;
    }
    setPromoteBusy(true);
    setPromoteError(null);
    try {
      const r = await api.finalizeDeck({
        slug: deckState.slug,
        npc_id: npcId,
        name: deckState.name,
      });
      setPromoteResult(r);
      // After promote the run is gone; clear local state to reflect that.
      setDeckState(null);
      await refreshRuns();
    } catch (e) {
      setPromoteError(e instanceof Error ? e.message : String(e));
    } finally {
      setPromoteBusy(false);
    }
  }, [deckState, npcId, refreshRuns]);

  const onDeleteRun = useCallback(
    async (slugToDelete: string) => {
      if (
        !window.confirm(
          `Delete the run "${slugToDelete}"? This wipes its scratch dir and every gen/ candidate for it. Not reversible.`,
        )
      ) {
        return;
      }
      setDeletingSlug(slugToDelete);
      try {
        await api.deleteDeckRun(slugToDelete);
        // If we just deleted the slug currently loaded in the form,
        // clear the local state so the page shows the blank "no run
        // yet" view rather than stale slots.
        if (deckState?.slug === slugToDelete) {
          setDeckState(null);
        }
        await refreshRuns();
      } catch (e) {
        setRunsError(e instanceof Error ? e.message : String(e));
      } finally {
        setDeletingSlug(null);
      }
    },
    [deckState?.slug, refreshRuns],
  );

  // --- derived ---

  const chosenCount = useMemo(() => {
    if (!deckState) return 0;
    return deckState.slots.filter((s) => s.chosen).length;
  }, [deckState]);

  const totalSlots = deckState?.slots.length ?? 0;
  const allChosen = totalSlots > 0 && chosenCount === totalSlots;
  const totalCards = useMemo(() => {
    if (!deckState) return 0;
    return deckState.slots.reduce((acc, s) => acc + (s.brief?.count ?? 0), 0);
  }, [deckState]);

  const architecting = !!architectJobId;
  const canArchitect = !architecting && slug.trim() !== "" && !!faction;

  // --- render ---

  return (
    // position:absolute fills the parent grid cell (AdminToolsTab's
    // right column) regardless of the cell's intrinsic auto-row sizing,
    // which is why overflowY:auto here actually scrolls instead of
    // letting the page stretch indefinitely.
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflowY: "auto",
        padding: 16,
      }}
    >
      <Header theme={theme} />

      <section
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdge}`,
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 200px) minmax(0, 110px) minmax(0, 110px)",
            gap: 12,
          }}
        >
          <Field theme={theme} label="Slug">
            <TextInput
              theme={theme}
              value={slug}
              onChange={setSlug}
              placeholder="aelvian_standing_watch"
            />
          </Field>
          <Field theme={theme} label="Faction">
            <Select
              theme={theme}
              value={faction}
              onChange={setFaction}
              options={FACTIONS.map((f) => ({ value: f, label: f }))}
            />
          </Field>
          <Field theme={theme} label="Deck size">
            <NumberInput
              theme={theme}
              value={deckSize}
              onChange={setDeckSize}
            />
          </Field>
          <Field theme={theme} label="Copy cap">
            <NumberInput theme={theme} value={copyCap} onChange={setCopyCap} />
          </Field>
        </div>

        <Field theme={theme} label="Theme / designer notes">
          <TextArea
            theme={theme}
            value={theme_}
            onChange={setThemeText}
            placeholder="Rooted-token ramp into mass-Magicproof finishers…"
          />
        </Field>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onArchitect}
            disabled={!canArchitect}
            {...primaryBtn(theme, !canArchitect)}
          >
            {architecting ? (
              <AnimatedEllipsis label="ARCHITECTING" />
            ) : (
              "ARCHITECT THIS DECK"
            )}
          </button>
          {architectError && (
            <Banner theme={theme} kind="error">
              {architectError}
            </Banner>
          )}
        </div>
      </section>

      <RunsList
        theme={theme}
        runs={runs}
        runsError={runsError}
        deletingSlug={deletingSlug}
        currentSlug={slug}
        onPick={(s) => setSlug(s)}
        onDelete={onDeleteRun}
      />

      {deckState && (
        <section
          style={{
            background: theme.panel,
            border: `2px solid ${theme.panelEdge}`,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {deckState.name}
            </div>
            <div style={{ color: theme.inkDim, fontSize: 11 }}>
              {chosenCount} / {totalSlots} slots chosen · {totalCards} /{" "}
              {deckState.deck_size} cards
            </div>
          </div>
          <div style={{ color: theme.inkDim, fontSize: 12 }}>
            {deckState.gameplan_blurb}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Field theme={theme} label="k per generate">
              <NumberInput theme={theme} value={k} onChange={setK} />
            </Field>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {deckState.slots.map((slot) => (
              <SlotRow
                key={slot.idx}
                slot={slot}
                expanded={expanded.has(slot.idx)}
                onToggleExpand={() =>
                  setExpanded((prev) => {
                    const n = new Set(prev);
                    if (n.has(slot.idx)) n.delete(slot.idx);
                    else n.add(slot.idx);
                    return n;
                  })
                }
                editingBrief={editingBrief.has(slot.idx)}
                onToggleEditBrief={() =>
                  setEditingBrief((prev) => {
                    const n = new Set(prev);
                    if (n.has(slot.idx)) n.delete(slot.idx);
                    else n.add(slot.idx);
                    return n;
                  })
                }
                onSaveBrief={(b) => onSaveBrief(slot.idx, b)}
                jobInFlight={!!slotJobIds[slot.idx]}
                error={slotErrors[slot.idx] ?? null}
                onGenerate={() => onGenerateSlot(slot)}
                onKeep={(cardId) => onKeep(slot.idx, cardId)}
                onSetCount={(count) => onSetCount(slot.idx, count)}
                onInspect={(card) => setInspect(card)}
                onEditCard={onEditCard}
                onRegenImage={onRegenImage}
                regenInFlight={regenInFlight}
                catalogById={catalogById}
              />
            ))}
          </div>
        </section>
      )}

      {deckState && (
        <section
          style={{
            background: theme.panel,
            border: `2px solid ${theme.panelEdge}`,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 11, color: theme.inkFaint, letterSpacing: theme.letterSpacing }}>
            RATING
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={onRate}
              disabled={!allChosen || ratingBusy}
              {...defaultBtn(theme, !allChosen || ratingBusy)}
            >
              {ratingBusy ? "RATING…" : "RATE THIS DECK"}
            </button>
            {!allChosen && (
              <div style={{ color: theme.inkDim, fontSize: 11 }}>
                Pick a candidate for every slot first.
              </div>
            )}
          </div>
          {ratingError && (
            <Banner theme={theme} kind="error">
              {ratingError}
            </Banner>
          )}
          {rating && rating.rating != null && (
            <div style={{ fontSize: 12, color: theme.ink }}>
              <strong>BT rating:</strong> {rating.rating.toFixed(3)} · win rate{" "}
              {rating.win_rate != null
                ? `${(rating.win_rate * 100).toFixed(1)}%`
                : "—"}{" "}
              ({rating.wins}/{rating.games_played} games, {rating.cohort_size}
              -deck cohort, {rating.elapsed_secs.toFixed(1)}s)
            </div>
          )}
        </section>
      )}

      {deckState && (
        <section
          style={{
            background: theme.panel,
            border: `2px solid ${theme.panelEdge}`,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 11, color: theme.inkFaint, letterSpacing: theme.letterSpacing }}>
            PROMOTE
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Field theme={theme} label="NPC">
              <Select
                theme={theme}
                value={npcId}
                onChange={setNpcId}
                options={[
                  { value: "", label: "— pick an NPC —" },
                  ...npcOptions.map((n) => ({
                    value: n.id,
                    label: `${n.name ?? n.id} (${n.id})`,
                  })),
                ]}
              />
            </Field>
            <button
              onClick={onFinalize}
              disabled={!allChosen || !npcId || promoteBusy}
              {...primaryBtn(theme, !allChosen || !npcId || promoteBusy)}
            >
              {promoteBusy ? "PROMOTING…" : "PROMOTE"}
            </button>
          </div>
          {promoteError && (
            <Banner theme={theme} kind="error">
              {promoteError}
            </Banner>
          )}
          {promoteResult && (
            <Banner theme={theme} kind="info">
              Promoted {promoteResult.cards.length} cards · yaml at{" "}
              {promoteResult.deck_path}
            </Banner>
          )}
        </section>
      )}

      <Inspector
        card={inspect}
        theme={theme}
        onClose={() => setInspect(null)}
      />
    </div>
  );
}

function Header({ theme }: { theme: Theme }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>+ Build Deck with AI</div>
      <div style={{ fontSize: 11, color: theme.inkDim }}>
        Architect AI sketches the slot list; card-gen AI fills each slot with
        candidates; you choose, edit, and promote.
      </div>
    </div>
  );
}

// =====================================================================
// One slot row
// =====================================================================

function SlotRow({
  slot,
  expanded,
  onToggleExpand,
  editingBrief,
  onToggleEditBrief,
  onSaveBrief,
  jobInFlight,
  error,
  onGenerate,
  onKeep,
  onSetCount,
  onInspect,
  onEditCard,
  onRegenImage,
  regenInFlight,
  catalogById,
}: {
  slot: DeckSlot;
  expanded: boolean;
  onToggleExpand: () => void;
  editingBrief: boolean;
  onToggleEditBrief: () => void;
  onSaveBrief: (b: SlotBrief) => void;
  jobInFlight: boolean;
  error: string | null;
  onGenerate: () => void;
  onKeep: (cardId: string) => void;
  onSetCount: (n: number) => void;
  onInspect: (c: CatalogCard) => void;
  onEditCard: (cardId: string) => void;
  onRegenImage: (cardId: string) => void;
  regenInFlight: Set<string>;
  catalogById: Record<string, CatalogCard>;
}) {
  const theme = pixelTheme;
  const status = slot.chosen ? "chosen" : "pending";
  const chosenCard = slot.chosen ? catalogById[slot.chosen] : null;

  return (
    <div
      style={{
        border: `2px solid ${slot.chosen ? theme.accent : theme.panelEdge}`,
        background: theme.bgDeep,
        padding: 8,
      }}
    >
      {/* The whole row header is the toggle target. No tiny caret —
          clicking anywhere on the summary expands/collapses. */}
      <div
        onClick={onToggleExpand}
        style={{
          display: "grid",
          gridTemplateColumns: "60px 70px 110px minmax(0, 1fr) auto",
          gap: 12,
          alignItems: "center",
          cursor: "pointer",
          padding: "4px 0",
          userSelect: "none",
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 12,
            color: theme.inkFaint,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {expanded ? "▼ #" : "▶ #"}
          {slot.idx.toString().padStart(2, "0")}
        </div>
        <div style={{ fontSize: 11, color: theme.inkDim }}>
          {status === "chosen" ? (
            <span style={{ color: theme.accent }}>✓ chosen</span>
          ) : jobInFlight ? (
            <AnimatedEllipsis label="running" />
          ) : (
            "pending"
          )}
        </div>
        <div style={{ fontSize: 11, color: theme.inkDim }}>
          cost {slot.brief.cost} · {slot.brief.card_type ?? "any"} ·{" "}
          {slot.brief.rarity ?? "any"} · ×{slot.brief.count}
        </div>
        <div
          style={{
            fontSize: 11,
            color: theme.ink,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {chosenCard ? chosenCard.name : slot.brief.notes}
        </div>
        <div
          style={{ display: "flex", gap: 4 }}
          // Don't let button clicks toggle the row.
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onGenerate}
            disabled={jobInFlight}
            {...defaultBtn(theme, jobInFlight)}
          >
            {jobInFlight ? (
              <AnimatedEllipsis label="GENERATING" />
            ) : slot.candidates.length === 0 ? (
              "GENERATE"
            ) : (
              "GENERATE MORE"
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
          }}
        >
          {!editingBrief && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 11, color: theme.inkDim, flex: 1 }}>
                <strong>Notes:</strong> {slot.brief.notes}
              </div>
              <button
                onClick={onToggleEditBrief}
                disabled={jobInFlight}
                {...defaultBtn(theme, jobInFlight)}
              >
                EDIT BRIEF
              </button>
            </div>
          )}
          {editingBrief && (
            <BriefEditor
              brief={slot.brief}
              onCancel={onToggleEditBrief}
              onSave={onSaveBrief}
            />
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Field theme={theme} label="Count in deck">
              <NumberInput
                theme={theme}
                value={String(slot.brief.count)}
                onChange={(v) => {
                  const n = Number(v);
                  if (Number.isInteger(n) && n > 0) onSetCount(n);
                }}
              />
            </Field>
          </div>

          {slot.candidates.length === 0 && (
            <div style={{ color: theme.inkDim, fontSize: 11 }}>
              No candidates yet. Click GENERATE to write the first ones.
            </div>
          )}
          {slot.candidates.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              {slot.candidates.map((c) => {
                const cc = catalogById[c.card_id];
                const isChosen = slot.chosen === c.card_id;
                return (
                  <CandidateTile
                    key={c.card_id}
                    cardId={c.card_id}
                    catalog={cc ?? null}
                    isChosen={isChosen}
                    onChoose={() => onKeep(c.card_id)}
                    onInspect={() => cc && onInspect(cc)}
                    onEdit={() => onEditCard(c.card_id)}
                    onRegenImage={() => onRegenImage(c.card_id)}
                    regenRunning={regenInFlight.has(c.card_id)}
                  />
                );
              })}
            </div>
          )}

          {error && (
            <Banner theme={theme} kind="error">
              {error}
            </Banner>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// One candidate: real card render + radio selection + Edit / Regen Image
// =====================================================================

function CandidateTile({
  cardId,
  catalog,
  isChosen,
  onChoose,
  onInspect,
  onEdit,
  onRegenImage,
  regenRunning,
}: {
  cardId: string;
  catalog: CatalogCard | null;
  isChosen: boolean;
  onChoose: () => void;
  onInspect: () => void;
  onEdit: () => void;
  onRegenImage: () => void;
  regenRunning: boolean;
}) {
  const theme = pixelTheme;
  const cardWidth = naturalCardWidthPx("small");

  return (
    <div
      style={{
        width: cardWidth,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 6,
        border: `2px solid ${isChosen ? theme.accent : "transparent"}`,
        background: isChosen ? theme.panel : "transparent",
      }}
    >
      {/* Radio + name row at the top */}
      <label
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          fontSize: 11,
          color: theme.ink,
          fontFamily: theme.fontDisplay,
          letterSpacing: theme.letterSpacing,
        }}
      >
        <input
          type="radio"
          checked={isChosen}
          onChange={onChoose}
          style={{ cursor: "pointer" }}
        />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
          title={catalog?.name ?? cardId}
        >
          {catalog?.name ?? cardId}
        </span>
      </label>

      {/* Actual card render. Click → open Inspector. */}
      <div
        onClick={onInspect}
        style={{ cursor: "pointer" }}
        title="click to inspect"
      >
        {catalog ? (
          <Card card={catalogToCardView(catalog)} size="small" theme={theme} />
        ) : (
          <div
            style={{
              width: cardWidth,
              height: Math.round(cardWidth * 1.4),
              border: `1px dashed ${theme.panelEdge}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.inkFaint,
              fontSize: 11,
            }}
          >
            loading…
          </div>
        )}
      </div>

      {/* Per-candidate action row. */}
      <div style={{ display: "flex", gap: 4 }}>
        <button onClick={onEdit} {...defaultBtn(theme)}>
          EDIT
        </button>
        <button
          onClick={onRegenImage}
          disabled={regenRunning}
          {...defaultBtn(theme, regenRunning)}
        >
          {regenRunning ? (
            <AnimatedEllipsis label="IMG" />
          ) : (
            "REGEN IMAGE"
          )}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Brief editor (cost / type / rarity / count / notes)
// =====================================================================

function BriefEditor({
  brief,
  onCancel,
  onSave,
}: {
  brief: SlotBrief;
  onCancel: () => void;
  onSave: (b: SlotBrief) => void;
}) {
  const theme = pixelTheme;
  const [cost, setCost] = useState(String(brief.cost));
  const [cardType, setCardType] = useState(brief.card_type ?? "");
  const [rarity, setRarity] = useState(brief.rarity ?? "");
  const [count, setCount] = useState(String(brief.count));
  const [notes, setNotes] = useState(brief.notes);

  const submit = () => {
    onSave({
      cost: Number(cost) || 0,
      card_type:
        (CARD_TYPES.includes(cardType as SlotBrief["card_type"])
          ? (cardType as SlotBrief["card_type"])
          : null) || null,
      rarity:
        (RARITIES.includes(rarity as SlotBrief["rarity"])
          ? (rarity as SlotBrief["rarity"])
          : null) || null,
      count: Number(count) || brief.count,
      notes,
    });
  };

  return (
    <div
      style={{
        background: theme.bgDeep,
        padding: 8,
        border: `2px solid ${theme.panelEdge}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 100px) minmax(0, 150px) minmax(0, 150px) minmax(0, 100px)",
          gap: 8,
        }}
      >
        <Field theme={theme} label="Cost">
          <NumberInput theme={theme} value={cost} onChange={setCost} />
        </Field>
        <Field theme={theme} label="Type">
          <Select
            theme={theme}
            value={cardType ?? ""}
            onChange={setCardType}
            options={[
              { value: "", label: "any" },
              ...CARD_TYPES.map((t) => ({ value: t as string, label: t as string })),
            ]}
          />
        </Field>
        <Field theme={theme} label="Rarity">
          <Select
            theme={theme}
            value={rarity ?? ""}
            onChange={setRarity}
            options={[
              { value: "", label: "any" },
              ...RARITIES.map((r) => ({ value: r as string, label: r as string })),
            ]}
          />
        </Field>
        <Field theme={theme} label="Count">
          <NumberInput theme={theme} value={count} onChange={setCount} />
        </Field>
      </div>
      <Field theme={theme} label="Notes">
        <TextArea theme={theme} value={notes} onChange={setNotes} />
      </Field>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={submit} {...primaryBtn(theme)}>
          SAVE BRIEF
        </button>
        <button onClick={onCancel} {...defaultBtn(theme)}>
          CANCEL
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Runs picker — every in-progress build-deck-with-ai run
// =====================================================================

function RunsList({
  theme,
  runs,
  runsError,
  deletingSlug,
  currentSlug,
  onPick,
  onDelete,
}: {
  theme: Theme;
  runs: DeckRunSummary[];
  runsError: string | null;
  deletingSlug: string | null;
  currentSlug: string;
  onPick: (slug: string) => void;
  onDelete: (slug: string) => void;
}) {
  if (runsError) {
    return (
      <section
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdge}`,
          padding: 12,
        }}
      >
        <Banner theme={theme} kind="error">
          Failed to list runs: {runsError}
        </Banner>
      </section>
    );
  }

  if (runs.length === 0) {
    return null;
  }

  return (
    <section
      style={{
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: theme.inkFaint,
          letterSpacing: theme.letterSpacing,
        }}
      >
        IN-PROGRESS RUNS ({runs.length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {runs.map((r) => {
          const isCurrent = r.slug === currentSlug.trim();
          const complete = r.total > 0 && r.chosen === r.total;
          return (
            <div
              key={r.slug}
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(0, 1fr) 90px 90px 100px 70px",
                gap: 12,
                alignItems: "center",
                padding: "6px 8px",
                background: isCurrent ? theme.bgDeep : "transparent",
                border: `1px solid ${
                  isCurrent ? theme.accent : theme.panelEdge
                }`,
                cursor: "pointer",
              }}
              onClick={() => onPick(r.slug)}
            >
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  color: theme.ink,
                }}
              >
                <strong>{r.slug}</strong>
                {r.name && r.name !== r.slug && (
                  <span style={{ color: theme.inkDim }}> — {r.name}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: theme.inkDim }}>
                {r.faction ?? "—"}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: complete ? theme.accent : theme.inkDim,
                }}
              >
                {r.chosen} / {r.total} chosen
              </div>
              <div style={{ fontSize: 11, color: theme.inkDim }}>
                {r.updated_at
                  ? new Date(r.updated_at * 1000).toLocaleString()
                  : "—"}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => onDelete(r.slug)}
                  disabled={deletingSlug === r.slug}
                  {...defaultBtn(theme, deletingSlug === r.slug)}
                >
                  {deletingSlug === r.slug ? (
                    <AnimatedEllipsis label="DEL" />
                  ) : (
                    "DELETE"
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
